import { useState } from "react";
import { useClubStatus } from "../hooks/useUserDetail";
import { apiSend } from "../lib/api";

function DiagIcon({ section }: { section: { ok?: boolean; error?: unknown; exists?: boolean } }) {
  if (section.ok) return <div className="diag-icon diag-ok"><i className="ph ph-check" /></div>;
  if (section.error) return <div className="diag-icon diag-error">?</div>;
  if (section.exists && !section.ok) return <div className="diag-icon diag-warn">!</div>;
  return <div className="diag-icon diag-missing"><i className="ph ph-x" /></div>;
}

function fmtDate(d: unknown): string {
  if (!d) return "N/A";
  const dt = typeof d === "number" ? new Date(d * 1000) : new Date(d as string);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const Warn = ({ children }: { children: React.ReactNode }) => <span className="diag-warn-text">{children}</span>;

export function ClubDiagnostic({ customerId }: { customerId: string }) {
  const { data, isLoading, error } = useClubStatus(customerId, true);
  const [auth0Btn, setAuth0Btn] = useState<{ text: string; color?: string; disabled?: boolean }>({ text: "Create Auth0 Account" });
  const [resync, setResync] = useState<Record<string, { text: string; color?: string; disabled?: boolean }>>({});

  if (isLoading) return <div className="club-diagnostic-loading"><div className="loading-message">Loading club account diagnostics...</div></div>;
  if (error || !data || data.error || !data.isClubMember) return null;

  async function createAuth0() {
    setAuth0Btn({ text: "Creating...", disabled: true });
    try {
      const resp = await fetch(`/api/users/${encodeURIComponent(customerId)}/create-auth0`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const d = await resp.json();
      if (resp.ok && d.success) setAuth0Btn({ text: "✓ Created" + (d.passwordResetSent ? " (email sent)" : ""), color: "#27ae60", disabled: true });
      else setAuth0Btn({ text: d.error || "Failed", color: "#e74c3c" });
    } catch {
      setAuth0Btn({ text: "Error", color: "#e74c3c" });
    }
  }

  async function resyncFolder(customerName: string, orderPath: string) {
    setResync((r) => ({ ...r, [orderPath]: { text: "Syncing...", disabled: true } }));
    try {
      const d = await apiSend<any>(`users/${encodeURIComponent(customerId)}/resync-folder`, "POST", { customerName, orderPath });
      if (d.ok) setResync((r) => ({ ...r, [orderPath]: { text: "✓ Synced", color: "#27ae60", disabled: true } }));
      else setResync((r) => ({ ...r, [orderPath]: { text: `Failed (${d.status || "error"})`, color: "#e74c3c" } }));
    } catch {
      setResync((r) => ({ ...r, [orderPath]: { text: "Error", color: "#e74c3c" } }));
    }
  }

  const s = data.stripe || {};
  const a = data.auth0 || {};
  const du = data.d1User || {};
  const da = data.d1Access || {};
  const kv = data.kvMapping || {};
  const r2 = data.r2 || {};
  const idx = data.indexing;

  // Folder indexing computation (ported).
  const indexedPaths: { path: string }[] = (idx && idx.indexedOrderPaths) || [];
  const indexedSet = new Set(indexedPaths.map((p) => p.path));
  let unindexed: string[] = [];
  if (r2.folders && Object.keys(r2.folders).length > 0) {
    Object.keys(r2.folders).forEach((f) => {
      if (f !== ".keep" && !indexedSet.has(f)) unindexed.push(f);
    });
  }
  let indexNode: React.ReactNode;
  if (!idx || !idx.exists) {
    const allFolders = r2.folders ? Object.keys(r2.folders).filter((f) => f !== ".keep") : [];
    if (allFolders.length > 0) {
      indexNode = <Warn>{`0 files indexed but ${allFolders.length} order folder(s) in R2`}</Warn>;
      unindexed = allFolders;
    } else indexNode = "No entries in file_metadata_temp and no order folders in R2";
  } else {
    const rec = idx.record;
    indexNode = (
      <>
        {rec.indexedFileCount} files indexed across {indexedPaths.length} order(s)
        {rec.date_entered && <> · Last indexed: {fmtDate(rec.date_entered)}</>}
        {unindexed.length > 0 && <><br /><Warn>{`${unindexed.length} folder(s) not indexed`}</Warn></>}
      </>
    );
  }
  const idxStatus = unindexed.length > 0 ? { ok: false, exists: true } : idx && idx.exists ? { ok: true, exists: true } : { ok: false, exists: false };

  const btnStyle = (color?: string, accent = "#37358F"): React.CSSProperties => ({
    margin: "3px 4px 3px 0",
    padding: "3px 10px",
    fontSize: 11,
    border: `1px solid ${color || accent}`,
    background: "white",
    color: color || accent,
    borderRadius: 4,
    cursor: "pointer",
  });

  return (
    <details className="club-diagnostic-card" id="clubDiagnosticSection">
      <summary><h2>Club Account Status</h2></summary>
      <div className="diag-grid">
        <DiagRow icon={<DiagIcon section={s} />} label="Stripe Subscription">
          {s.error ? <Warn>{String(s.error)}</Warn> : !s.exists ? (
            <>No Stripe customer found for <code>{data.email}</code></>
          ) : (
            <>
              {s.subscriptions?.[0] ? (
                <>
                  Status: <code>{s.subscriptions[0].status}</code> · Sub: <code>{s.subscriptions[0].id}</code>
                  {s.subscriptions[0].currentPeriodEnd && <> · Renews: {fmtDate(s.subscriptions[0].currentPeriodEnd)}</>}
                  {s.subscriptions[0].cancelAtPeriodEnd && <> · <Warn>Cancels at period end</Warn></>}
                </>
              ) : (
                <>Customer exists (<code>{s.customerId}</code>) but no subscriptions</>
              )}{" "}
              · <a href={`https://dashboard.stripe.com/customers/${encodeURIComponent(s.customerId)}`} target="_blank" rel="noreferrer">View in Stripe</a>
            </>
          )}
        </DiagRow>

        <DiagRow icon={<DiagIcon section={a} />} label="Auth0 Account">
          {a.error ? <Warn>{String(a.error)}</Warn> : !a.exists ? (
            <>
              No Auth0 account found for <code>{data.email}</code>{" "}
              <button onClick={createAuth0} disabled={auth0Btn.disabled} style={{ ...btnStyle(auth0Btn.color), marginLeft: 6, opacity: auth0Btn.disabled ? 0.6 : 1 }}>
                {auth0Btn.text}
              </button>
            </>
          ) : (
            <>
              Verified: {a.emailVerified ? "✓" : <Warn>No</Warn>} · Logins: {a.loginsCount || 0}
              {a.lastLogin && <> · Last login: {fmtDate(a.lastLogin)}</>}
              {!a.emailVerified && <> · <Warn>Needs password reset</Warn></>}
            </>
          )}
        </DiagRow>

        <DiagRow icon={<DiagIcon section={du} />} label="D1 Users Record">
          {du.error ? <Warn>{String(du.error)}</Warn> : !du.exists ? (
            <>No D1 user record for <code>{data.email}</code> in tpfl tenant</>
          ) : (
            <>
              ID: <code>{du.id}</code>
              {du.subscriptionTier && <> · Tier: <code>{du.subscriptionTier}</code></>}
              {du.subscriptionStatus && <> · Status: <code>{du.subscriptionStatus}</code></>}
              {du.stripeCustomerId && <> · Stripe: <code>{du.stripeCustomerId}</code></>}
              {du.createdAt && <> · Created: {fmtDate(du.createdAt)}</>}
            </>
          )}
        </DiagRow>

        <DiagRow icon={<DiagIcon section={da} />} label="Tenant Access Record">
          {da.error ? <Warn>{String(da.error)}</Warn> : !da.exists ? (
            <>No user_tenant_access record for tpfl</>
          ) : (
            <>
              Access type: <code>{da.accessType || "default"}</code>
              {da.firstAccessAt && <> · First: {fmtDate(da.firstAccessAt)}</>}
              {da.lastAccessAt && <> · Last: {fmtDate(da.lastAccessAt)}</>}
            </>
          )}
        </DiagRow>

        <DiagRow icon={<DiagIcon section={kv} />} label="EMAIL_MAPPING KV">
          {kv.error ? <Warn>{String(kv.error)}</Warn> : !kv.exists ? (
            <>No EMAIL_MAPPING entry. Expected: <code>{data.customerName}</code> → <code>{data.email}</code></>
          ) : kv.backwards ? (
            <><Warn>Backwards mapping detected!</Warn> <code>{kv.key}</code> → <code>{kv.value}</code></>
          ) : (
            <><code>{kv.key}</code> → <code>{kv.value}</code></>
          )}
        </DiagRow>

        <DiagRow icon={<DiagIcon section={r2} />} label="R2 Customer Folder">
          {r2.error ? <Warn>{String(r2.error)}</Warn> : !r2.exists ? (
            <>No folder at <code>customers/{data.customerName}/</code></>
          ) : (
            <>
              .keep: {r2.keepExists ? "✓" : <Warn>missing</Warn>} · {r2.fileCount} objects
              {r2.folders && Object.keys(r2.folders).length > 0 && (
                <> · Folders: {Object.entries(r2.folders).map(([k, v]) => `${k} (${v})`).join(", ")}</>
              )}
            </>
          )}
        </DiagRow>

        <DiagRow icon={<DiagIcon section={idxStatus} />} label="Folder Indexing">
          {indexNode}
          {unindexed.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {unindexed.map((folder) => {
                const st = resync[folder];
                return (
                  <button
                    key={folder}
                    className="resync-btn"
                    disabled={st?.disabled}
                    onClick={() => resyncFolder(data.customerName, folder)}
                    style={{ ...btnStyle(st?.color, "#e67e22"), opacity: st?.disabled ? 0.6 : 1 }}
                  >
                    {st ? st.text : <>Resync <code>{folder}</code></>}
                  </button>
                );
              })}
            </div>
          )}
        </DiagRow>
      </div>
    </details>
  );
}

function DiagRow({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="diag-row">
      {icon}
      <div className="diag-info">
        <div className="diag-label">{label}</div>
        <div className="diag-detail">{children}</div>
      </div>
    </div>
  );
}
